import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import QRCode from "react-qr-code";
import { Trophy, Crown, Zap, Clock, Users, Sparkles, HelpCircle, CheckCircle, XCircle, Eye, Maximize, Minimize } from "lucide-react";
import confetti from 'canvas-confetti';
import { ThemeProvider, THEMES } from "../context/ThemeContext";
import { TeamAvatar } from "../components/TeamAvatar";
import Logo from "../components/Logo";
import { useServerSyncedTimer } from "../hooks/useServerSyncedTimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fullscreen toggle button component
const FullscreenButton = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };
  
  return (
    <button
      onClick={toggleFullscreen}
      className="fixed top-4 right-4 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all"
      title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
    >
      {isFullscreen ? (
        <Minimize className="w-6 h-6 text-white" />
      ) : (
        <Maximize className="w-6 h-6 text-white" />
      )}
    </button>
  );
};

// Play victory sound
const playVictorySound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.2, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    const now = audioContext.currentTime;
    playTone(392, now, 0.15);
    playTone(392, now + 0.15, 0.15);
    playTone(392, now + 0.3, 0.15);
    playTone(311, now + 0.45, 0.3);
    playTone(349, now + 0.75, 0.15);
    playTone(392, now + 0.9, 0.15);
    playTone(311, now + 1.05, 0.2);
    playTone(523, now + 1.25, 0.5);
  } catch (e) {}
};

// Play drum roll sound
const playDrumRoll = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const now = audioContext.currentTime;
    for (let i = 0; i < 8; i++) {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 150 + Math.random() * 50;
      oscillator.type = 'triangle';
      gainNode.gain.setValueAtTime(0.1, now + i * 0.08);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.06);
      oscillator.start(now + i * 0.08);
      oscillator.stop(now + i * 0.08 + 0.06);
    }
  } catch (e) {}
};

// Play buzz sound
const playBuzzSound = (isFirst) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    if (isFirst) {
      const playTone = (freq, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      const now = audioContext.currentTime;
      playTone(523, now, 0.12);
      playTone(659, now + 0.12, 0.12);
      playTone(784, now + 0.24, 0.12);
      playTone(1047, now + 0.36, 0.25);
    } else {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 440;
      oscillator.type = 'square';
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.08);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.08);
    }
  } catch (e) {}
};

// Trigger confetti
const triggerConfetti = () => {
  const duration = 5000;
  const end = Date.now() + duration;
  const colors = ['#FFD700', '#E91E8C', '#8B5CF6', '#00E676', '#FF1744', '#CCFF00'];
  (function frame() {
    confetti({ particleCount: 7, angle: 60, spread: 65, origin: { x: 0, y: 0.6 }, colors });
    confetti({ particleCount: 7, angle: 120, spread: 65, origin: { x: 1, y: 0.6 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  }());
};

const getTeamName = (team) => team.name || `Team ${team.number}`;

// Helper to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Helper to check if URL is a video file
const isVideoFile = (url) => {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
};

// Media Display Component for Questions
const QuestionMedia = ({ question }) => {
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  
  const youtubeId = getYouTubeVideoId(question.video_url);
  const isDirectVideo = isVideoFile(question.video_url);
  
  const toggleAudio = () => {
    if (audioRef.current) {
      if (audioPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setAudioPlaying(!audioPlaying);
    }
  };
  
  return (
    <div className="flex flex-col items-center gap-6 mb-6">
      {/* Image */}
      {question.image_url && (
        <motion.img
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          src={question.image_url}
          alt="Question media"
          className="max-w-2xl max-h-[400px] rounded-2xl object-contain shadow-2xl border-2 border-white/10"
          onError={(e) => e.target.style.display = 'none'}
        />
      )}
      
      {/* YouTube Video */}
      {youtubeId && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-3xl aspect-video rounded-2xl overflow-hidden shadow-2xl border-2 border-white/10"
        >
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=0&rel=0`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Question video"
          />
        </motion.div>
      )}
      
      {/* Direct Video */}
      {isDirectVideo && (
        <motion.video
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          src={question.video_url}
          controls
          className="max-w-3xl max-h-[400px] rounded-2xl shadow-2xl border-2 border-white/10"
        />
      )}
      
      {/* Audio */}
      {question.audio_url && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex items-center gap-4 glass-heavy rounded-full px-8 py-4"
        >
          <audio ref={audioRef} src={question.audio_url} onEnded={() => setAudioPlaying(false)} />
          <button
            onClick={toggleAudio}
            className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
              audioPlaying 
                ? 'bg-[#FF1744] scale-110' 
                : 'bg-[#00E676] hover:scale-105'
            }`}
          >
            {audioPlaying ? (
              <div className="w-5 h-5 bg-white rounded-sm" />
            ) : (
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          <span className="text-white text-lg">
            {audioPlaying ? 'Playing...' : 'Play Audio'}
          </span>
        </motion.div>
      )}
    </div>
  );
};

// Answer Feedback Component - shows correct/incorrect/revealed answer
const AnswerFeedback = ({ answerState, answer }) => {
  if (answerState === 'hidden') return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ scale: 0, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="mb-6"
      >
        {answerState === 'correct' && (
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 0.5, repeat: 3 }}
            className="inline-flex items-center gap-4 px-10 py-6 rounded-2xl bg-gradient-to-r from-[#00E676] to-[#00C853] shadow-2xl"
          >
            <CheckCircle className="w-16 h-16 text-white" />
            <span className="text-5xl font-bold text-white">CORRECT!</span>
          </motion.div>
        )}
        
        {answerState === 'incorrect' && (
          <motion.div
            animate={{ x: [-5, 5, -5, 5, 0] }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-4 px-10 py-6 rounded-2xl bg-gradient-to-r from-[#FF1744] to-[#D50000] shadow-2xl"
          >
            <XCircle className="w-16 h-16 text-white" />
            <span className="text-5xl font-bold text-white">WRONG!</span>
          </motion.div>
        )}
        
        {answerState === 'revealed' && answer && (
          <motion.div
            initial={{ rotateY: 90 }}
            animate={{ rotateY: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-heavy rounded-2xl p-8 border-2 border-[#8B5CF6]"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Eye className="w-8 h-8 text-[#8B5CF6]" />
              <span className="text-2xl text-[#E2D9F3]">The Answer Is...</span>
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-5xl md:text-6xl font-bold text-[#CCFF00] text-center"
            >
              {answer}
            </motion.p>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

// Aurora Background Component - Reshapers colors with CSS animation
// Get theme-specific aurora colors
const getAuroraColors = (theme) => {
  const themes = {
    reshapers: {
      // #674A9D, #854097, #A2408A
      color1: 'rgba(103, 74, 157, 0.5)',   // #674A9D
      color2: 'rgba(133, 64, 151, 0.5)',   // #854097
      color3: 'rgba(162, 64, 138, 0.4)',   // #A2408A
      color4: 'rgba(103, 74, 157, 0.3)',   // #674A9D lighter
      color5: 'rgba(133, 64, 151, 0.4)',   // #854097
    },
    teambuilding: {
      // #FFB902, #FF9602, #FF7403
      color1: 'rgba(255, 185, 2, 0.5)',    // #FFB902
      color2: 'rgba(255, 150, 2, 0.5)',    // #FF9602
      color3: 'rgba(255, 116, 3, 0.4)',    // #FF7403
      color4: 'rgba(255, 185, 2, 0.3)',    // #FFB902 lighter
      color5: 'rgba(255, 150, 2, 0.4)',    // #FF9602
    },
    marcom: {
      // #4FB4CC, #1C54CD, #2A70AB, #439A7A
      color1: 'rgba(79, 180, 204, 0.5)',   // #4FB4CC
      color2: 'rgba(28, 84, 205, 0.5)',    // #1C54CD
      color3: 'rgba(42, 112, 171, 0.4)',   // #2A70AB
      color4: 'rgba(67, 154, 122, 0.3)',   // #439A7A
      color5: 'rgba(79, 180, 204, 0.4)',   // #4FB4CC
    },
  };
  return themes[theme] || themes.reshapers;
};

const AuroraBackground = ({ theme = 'reshapers' }) => {
  const colors = getAuroraColors(theme);
  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      {/* Aurora gradient layers */}
      <div 
        className="absolute inset-0 opacity-60"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 20% 40%, ${colors.color1} 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 70% 60%, ${colors.color2} 0%, transparent 50%),
            radial-gradient(ellipse 70% 50% at 50% 30%, ${colors.color3} 0%, transparent 50%),
            radial-gradient(ellipse 50% 60% at 80% 20%, ${colors.color4} 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 30% 70%, ${colors.color5} 0%, transparent 50%)
          `,
          animation: 'aurora 15s ease-in-out infinite',
        }}
      />
      <div 
        className="absolute inset-0 opacity-40"
        style={{
          background: `
            radial-gradient(ellipse 70% 60% at 60% 50%, ${colors.color4} 0%, transparent 50%),
            radial-gradient(ellipse 50% 40% at 30% 30%, ${colors.color5} 0%, transparent 50%),
            radial-gradient(ellipse 80% 50% at 80% 70%, ${colors.color1} 0%, transparent 50%)
          `,
          animation: 'aurora 12s ease-in-out infinite reverse',
        }}
      />
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 40% 60%, ${colors.color3} 0%, transparent 50%),
            radial-gradient(ellipse 70% 40% at 70% 40%, ${colors.color4} 0%, transparent 50%)
          `,
          animation: 'aurora 18s ease-in-out infinite',
          animationDelay: '-5s',
        }}
      />
      {/* Add the keyframes via style tag */}
      <style>{`
        @keyframes aurora {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(5%, -5%) scale(1.05);
          }
          50% {
            transform: translate(-3%, 3%) scale(0.95);
          }
          75% {
            transform: translate(-5%, -3%) scale(1.02);
          }
        }
      `}</style>
    </div>
  );
};

// Dramatic Circular Countdown Timer Component
const CircularCountdownTimer = ({ timeRemaining, totalTime = 30 }) => {
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const progress = timeRemaining / totalTime;
  const strokeDashoffset = circumference * (1 - progress);
  
  const getTimerColor = () => {
    if (timeRemaining <= 5) return "#FF1744";
    if (timeRemaining <= 10) return "#FF9800";
    return "#00E676";
  };

  return (
    <motion.div
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      className="relative flex items-center justify-center"
    >
      <svg
        width="140"
        height="140"
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx="70"
          cy="70"
          r={radius}
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth="8"
          fill="transparent"
        />
        {/* Progress circle */}
        <motion.circle
          cx="70"
          cy="70"
          r={radius}
          stroke={getTimerColor()}
          strokeWidth="8"
          fill="transparent"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          animate={{
            strokeDashoffset: strokeDashoffset,
            stroke: getTimerColor()
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{
            filter: timeRemaining <= 5 ? "drop-shadow(0 0 10px #FF1744)" : "none"
          }}
        />
      </svg>
      
      {/* Timer text */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        animate={timeRemaining <= 5 ? { scale: [1, 1.1, 1] } : {}}
        transition={{ duration: 0.5, repeat: timeRemaining <= 5 ? Infinity : 0 }}
      >
        <span className={`text-4xl font-bold ${
          timeRemaining <= 5 ? 'text-[#FF1744]' : 
          timeRemaining <= 10 ? 'text-[#FF9800]' : 'text-[#00E676]'
        }`}>
          {timeRemaining}
        </span>
      </motion.div>
      
      {/* Pulsing ring effect for critical time */}
      {timeRemaining <= 5 && (
        <motion.div
          className="absolute inset-0 rounded-full border-4 border-[#FF1744]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.8, 0.2, 0.8]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
    </motion.div>
  );
};

const PresentationView = () => {
  const { sessionId } = useParams();
  const [state, setState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastBuzzCountRef = useRef(0);

  // Synced timer derived from state.server_time + state.timer_end_time
  const timer = useServerSyncedTimer(state);
  const timeRemaining = timer.active ? timer.seconds : null;
  
  // Final reveal state
  const [revealPhase, setRevealPhase] = useState('waiting'); // waiting, revealing, complete
  const [revealedTeams, setRevealedTeams] = useState([]);
  const [showWinner, setShowWinner] = useState(false);
  const lastModeRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/presentation`);
      const newState = response.data;
      
      // Play sound for new buzzes
      if (newState.buzzes.length > lastBuzzCountRef.current) {
        playBuzzSound(lastBuzzCountRef.current === 0);
      }
      lastBuzzCountRef.current = newState.buzzes.length;
      
      setState(newState);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching presentation state:", error);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchState();
    const interval = setInterval(fetchState, 1000);
    return () => clearInterval(interval);
  }, [fetchState]);

  // Handle mode changes for final reveal
  useEffect(() => {
    if (!state) return;
    
    // Detect transition to final mode
    if (state.mode === 'final' && lastModeRef.current !== 'final') {
      // Starting final reveal - reset and begin animation
      setRevealPhase('revealing');
      setRevealedTeams([]);
      setShowWinner(false);
      
      // Scoreboard is sorted by score descending: [1st, 2nd, 3rd, 4th]
      // We want to reveal from last place to first, so reverse it
      // Only show top 3 teams
      const top3 = state.scoreboard.slice(0, 3);
      const totalTeams = top3.length;
      
      let currentRank = totalTeams; // Start at 3rd place
      let currentIndex = totalTeams - 1; // Index into top3 (3rd place first)
      
      // Reveal each team with animation
      const revealNext = () => {
        if (currentIndex >= 0) {
          playDrumRoll();
          setTimeout(() => {
            // Add the team WITH its rank
            const team = top3[currentIndex];
            const teamWithRank = { ...team, rank: currentRank };
            setRevealedTeams(prev => [teamWithRank, ...prev]); // Prepend so newest appears at top
            
            currentIndex--;
            currentRank--;
            
            if (currentIndex >= 0) {
              setTimeout(revealNext, 1500); // Delay between reveals
            } else {
              // All revealed - show winner
              setTimeout(() => {
                setRevealPhase('complete');
                setShowWinner(true);
                playVictorySound();
                triggerConfetti();
              }, 1000);
            }
          }, 500);
        }
      };
      
      // Start revealing after a brief pause
      setTimeout(revealNext, 1000);
    }
    
    // Reset when leaving final mode
    if (state.mode !== 'final' && lastModeRef.current === 'final') {
      setRevealPhase('waiting');
      setRevealedTeams([]);
      setShowWinner(false);
    }
    
    lastModeRef.current = state.mode;
  }, [state?.mode, state?.scoreboard]);

  if (isLoading || !state) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#E91E8C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { mode, current_question, current_question_index, total_questions, buzzes, scoreboard, session, answer_state } = state;
  const joinUrl = `${window.location.origin}/join/${session.join_code}`;

  // Lobby View with QR Code and Aurora Animation
  if (mode === 'lobby') {
    return (
      <div className="h-screen w-screen bg-black flex flex-col items-center justify-center p-4 relative overflow-hidden">
        <FullscreenButton />
        <AuroraBackground theme={session?.theme} />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center relative z-10 w-full max-w-5xl"
        >
          <div className="mx-auto mb-4">
            <Logo size="xl" className="mx-auto" />
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-8xl font-bold gradient-text mb-2">{session.name}</h1>
          <p className="text-xl md:text-2xl lg:text-3xl text-[#E2D9F3] mb-8">Get ready to play!</p>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            {/* QR Code */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-heavy rounded-3xl p-6 md:p-8"
            >
              <p className="text-lg md:text-2xl text-[#E2D9F3] mb-4">Scan to Join</p>
              <div className="bg-white p-4 md:p-6 rounded-2xl">
                <QRCode value={joinUrl} size={200} />
              </div>
            </motion.div>
            
            {/* Join Code */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-heavy rounded-3xl p-6 md:p-8"
            >
              <p className="text-lg md:text-2xl text-[#E2D9F3] mb-2">Or enter code</p>
              <p className="text-5xl md:text-7xl lg:text-8xl font-mono font-bold text-[#CCFF00]">{session.join_code}</p>
            </motion.div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6 md:mt-8 flex items-center justify-center gap-4"
          >
            <Users className="w-8 h-8 md:w-10 md:h-10 text-[#E91E8C]" />
            <span className="text-xl md:text-3xl text-white">{session.groups.length} Teams</span>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Question View
  if (mode === 'question') {
    const hasMedia = current_question?.image_url || current_question?.video_url || current_question?.audio_url;
    
    return (
      <div className="h-screen w-screen bg-black flex flex-col items-center justify-center p-4 md:p-6 relative overflow-hidden">
        <FullscreenButton />
        <AuroraBackground theme={session?.theme} />
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl w-full text-center relative z-10 flex flex-col items-center justify-center"
        >
          <div className="mb-3">
            <span className="text-[#E91E8C] text-lg md:text-xl font-semibold">
              Question {current_question_index + 1} of {total_questions}
            </span>
          </div>
          
          {/* Answer Feedback - shows correct/incorrect/revealed */}
          <AnswerFeedback answerState={answer_state} answer={current_question?.answer} />
          
          {/* Media Display - smaller for fullscreen fit */}
          {hasMedia && current_question && answer_state === 'hidden' && (
            <div className="max-h-[30vh] overflow-hidden mb-4">
              <QuestionMedia question={current_question} />
            </div>
          )}
          
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="glass-heavy rounded-2xl p-4 md:p-8 mb-4 w-full"
          >
            {!hasMedia && answer_state === 'hidden' && <HelpCircle className="w-10 h-10 md:w-12 md:h-12 text-[#8B5CF6] mx-auto mb-3" />}
            <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
              {current_question?.text}
            </h1>
            {current_question?.points && answer_state === 'hidden' && (
              <p className="text-lg md:text-xl text-[#CCFF00] mt-3">{current_question.points} points</p>
            )}
          </motion.div>
          
          {timeRemaining !== null && answer_state === 'hidden' && (
            <CircularCountdownTimer 
              timeRemaining={timeRemaining} 
              totalTime={current_question?.time_limit || 30} 
            />
          )}
        </motion.div>
      </div>
    );
  }

  // Buzzer View
  if (mode === 'buzzer') {
    return (
      <div className="h-screen w-screen bg-black flex flex-col p-4 md:p-6 relative overflow-hidden">
        <FullscreenButton />
        <AuroraBackground theme={session?.theme} />
        
        {/* Question reminder */}
        {current_question && (
          <div className="text-center mb-3 relative z-10">
            <p className="text-base md:text-lg text-[#E2D9F3] mb-1">Question {current_question_index + 1}</p>
            <h2 className="text-lg md:text-2xl text-white font-semibold">{current_question.text}</h2>
          </div>
        )}
        
        {/* Answer Feedback */}
        <div className="text-center relative z-10">
          <AnswerFeedback answerState={answer_state} answer={current_question?.answer} />
        </div>
        
        {/* Timer */}
        {timeRemaining !== null && answer_state === 'hidden' && (
          <div className="text-center mb-3 relative z-10">
            <CircularCountdownTimer 
              timeRemaining={timeRemaining} 
              totalTime={current_question?.time_limit || 30} 
            />
          </div>
        )}
        
        {/* Buzz order */}
        <div className="flex-1 flex items-center justify-center relative z-10 overflow-auto">
          {buzzes.length === 0 ? (
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="text-center"
            >
              <Zap className="w-20 h-20 md:w-28 md:h-28 text-[#FF1744] mx-auto mb-4" />
              <p className="text-2xl md:text-3xl text-white font-bold">Waiting for buzz...</p>
            </motion.div>
          ) : (
            <div className="w-full max-w-xl space-y-3">
              {buzzes.slice(0, 4).map((buzz, index) => (
                <motion.div
                  key={buzz.id}
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center gap-4 p-4 rounded-xl backdrop-blur-sm ${
                    index === 0 
                      ? 'bg-gradient-to-r from-[#FFD700]/40 to-transparent border-2 border-[#FFD700]' 
                      : 'bg-white/10'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                    index === 0 ? 'bg-[#FFD700] text-black' : 'bg-white/20 text-white'
                  }`}>
                    {index === 0 ? <Trophy className="w-6 h-6" /> : index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-xl md:text-2xl font-bold text-white">{getTeamName({ number: buzz.group_number, name: session.groups.find(g => g.number === buzz.group_number)?.name })}</p>
                    <p className="text-sm text-[#E2D9F3]">{buzz.participant_name}</p>
                  </div>
                  {index === 0 && (
                    <span className="text-lg font-bold text-[#FFD700]">FIRST!</span>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Results View
  if (mode === 'results') {
    return (
      <div className="h-screen w-screen bg-black flex flex-col items-center justify-center p-4 md:p-6 relative overflow-hidden">
        <FullscreenButton />
        <AuroraBackground theme={session?.theme} />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="max-w-xl w-full relative z-10"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">Results</h2>
          
          {buzzes.length > 0 && (
            <div className="space-y-3">
              {buzzes.slice(0, 4).map((buzz, index) => {
                const team = session.groups.find(g => g.number === buzz.group_number);
                return (
                  <motion.div
                    key={buzz.id}
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.2 }}
                    className={`flex items-center gap-4 p-4 rounded-xl backdrop-blur-sm ${
                      index === 0 ? 'bg-gradient-to-r from-[#FFD700]/30 to-transparent border-2 border-[#FFD700]' : 'bg-white/10'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                      index === 0 ? 'bg-[#FFD700] text-black' : 'bg-white/20 text-white'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg md:text-xl font-bold text-white">{getTeamName(team || { number: buzz.group_number })}</p>
                    </div>
                    <div className="text-lg md:text-xl font-bold text-white">
                      {team?.score || 0} pts
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  // Carousel Overview View - Shows which group is at which station
  if (mode === 'carousel') {
    const groups = session?.groups || [];
    const stations = session?.stations || [];
    
    return (
      <div className="h-screen w-screen bg-black flex flex-col p-4 md:p-8 relative overflow-hidden">
        <FullscreenButton />
        <AuroraBackground theme={session?.theme} />
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6 relative z-10 flex-shrink-0"
        >
          <h2 className="text-3xl md:text-5xl font-bold gradient-text mb-2">Station Overview</h2>
          <p className="text-lg md:text-xl text-[#E2D9F3]">Where is everyone?</p>
        </motion.div>
        
        {/* Horizontal scrolling station cards */}
        <div className="flex-1 relative z-10 overflow-hidden flex flex-col">
          <div 
            className="flex-1 flex gap-4 md:gap-6 overflow-x-auto overflow-y-hidden pb-4 px-2"
            style={{ 
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
            data-testid="station-overview-scroll"
          >
            {stations.map((station, stationIndex) => {
              // Find groups at this station
              const groupsHere = groups.filter(g => g.current_station_index === stationIndex);
              
              return (
                <motion.div
                  key={station.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: stationIndex * 0.1 }}
                  className="glass-heavy rounded-2xl p-4 md:p-6 flex-shrink-0 flex flex-col"
                  style={{ 
                    width: stations.length <= 3 
                      ? `calc(${100 / stations.length}% - ${(stations.length - 1) * 24 / stations.length}px)` 
                      : 'min(380px, 75vw)',
                    scrollSnapAlign: 'start',
                  }}
                >
                  {/* Station Header */}
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10 flex-shrink-0">
                    <div 
                      className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center text-xl md:text-2xl font-bold text-white flex-shrink-0"
                      style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' }}
                    >
                      {stationIndex + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg md:text-2xl font-bold text-white truncate">{station.name}</h3>
                      <p className="text-sm text-[#E2D9F3]">
                        {groupsHere.length === 0 ? 'No groups' : 
                         groupsHere.length === 1 ? '1 group here' : 
                         `${groupsHere.length} groups here`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Groups at this station */}
                  <div className="space-y-3 flex-1 overflow-y-auto">
                    {groupsHere.length === 0 ? (
                      <div className="text-center py-6 text-[#6B5E7C]">
                        <p className="text-lg">Empty</p>
                      </div>
                    ) : (
                      groupsHere.map((group) => (
                        <motion.div
                          key={group.id}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                        >
                          <TeamAvatar 
                            teamNumber={group.number} 
                            avatarId={group.avatar_id}
                            size="md"
                            showBorder={false}
                          />
                          <div className="flex-1">
                            <p className="text-lg md:text-xl font-semibold text-white">
                              {group.name || `Team ${group.number}`}
                            </p>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
        
        {/* Timer if active */}
        {timeRemaining !== null && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 right-6 z-20"
          >
            <div className="glass-heavy rounded-2xl p-4 flex items-center gap-3">
              <Clock className="w-6 h-6 text-[#E91E8C]" />
              <span className={`text-3xl font-bold ${
                timeRemaining <= 30 ? 'text-[#FF1744]' : 'text-white'
              }`}>
                {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
              </span>
            </div>
          </motion.div>
        )}
      </div>
    );
  }

  // Scoreboard View
  if (mode === 'scoreboard') {
    return (
      <div className="h-screen w-screen bg-black flex flex-col items-center justify-center p-4 md:p-6 relative overflow-hidden">
        <FullscreenButton />
        <AuroraBackground theme={session?.theme} />
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="max-w-xl w-full relative z-10"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-8 h-8 md:w-10 md:h-10 text-[#FFD700]" />
            <h2 className="text-3xl md:text-4xl font-bold text-white">Scoreboard</h2>
          </div>
          
          <div className="space-y-3">
            {scoreboard.slice(0, 6).map((team, index) => (
              <motion.div
                key={team.number}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 backdrop-blur-sm ${
                  index === 0 ? 'border-[#FFD700] bg-gradient-to-r from-[#FFD700]/40 to-transparent' :
                  index === 1 ? 'border-[#C0C0C0] bg-gradient-to-r from-[#C0C0C0]/30 to-transparent' :
                  index === 2 ? 'border-[#CD7F32] bg-gradient-to-r from-[#CD7F32]/30 to-transparent' :
                  'border-white/20 bg-white/10'
                }`}
              >
                <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-2xl ${
                  index === 0 ? 'bg-[#FFD700] text-black' :
                  index === 1 ? 'bg-[#C0C0C0] text-black' :
                  index === 2 ? 'bg-[#CD7F32] text-black' :
                  'bg-white/20 text-white'
                }`}>
                  {index === 0 ? <Crown className="w-8 h-8" /> : index + 1}
                </div>
                {/* Team Avatar */}
                <TeamAvatar 
                  teamNumber={team.number} 
                  avatarId={team.avatar_id}
                  size="lg"
                  showBorder={false}
                />
                <div className="flex-1">
                  <p className="text-3xl font-bold text-white">{getTeamName(team)}</p>
                </div>
                <div className="text-4xl font-bold text-white">
                  {team.score}
                  <span className="text-xl text-[#E2D9F3] ml-2">pts</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  // Final Scores View with dramatic reveal animation
  if (mode === 'final') {
    const winner = scoreboard[0];
    
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-8 relative overflow-hidden">
        {/* Aurora Background */}
        <AuroraBackground theme={session?.theme} />
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="max-w-2xl w-full relative z-10"
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <Sparkles className="w-10 h-10 text-[#FFD700]" />
            <h2 className="text-5xl font-bold text-white">Final Results</h2>
            <Sparkles className="w-10 h-10 text-[#FFD700]" />
          </div>
          
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {revealedTeams.map((team) => {
                const actualRank = team.rank;
                const isWinner = actualRank === 1;
                
                return (
                  <motion.div
                    key={team.number}
                    initial={{ x: -200, opacity: 0, scale: 0.5, rotateY: -90 }}
                    animate={{ x: 0, opacity: 1, scale: 1, rotateY: 0 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 200, 
                      damping: 20,
                      duration: 0.8
                    }}
                    className={`flex items-center gap-6 p-6 rounded-2xl border-2 backdrop-blur-sm ${
                      actualRank === 1 ? 'border-[#FFD700] bg-gradient-to-r from-[#FFD700]/50 to-[#FFA500]/30' :
                      actualRank === 2 ? 'border-[#C0C0C0] bg-gradient-to-r from-[#C0C0C0]/40 to-transparent' :
                      actualRank === 3 ? 'border-[#CD7F32] bg-gradient-to-r from-[#CD7F32]/40 to-transparent' :
                      'border-white/20 bg-white/15'
                    }`}
                  >
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, type: "spring" }}
                      className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-2xl ${
                        actualRank === 1 ? 'bg-[#FFD700] text-black' :
                        actualRank === 2 ? 'bg-[#C0C0C0] text-black' :
                        actualRank === 3 ? 'bg-[#CD7F32] text-black' :
                        'bg-white/20 text-white'
                      }`}
                    >
                      {actualRank === 1 ? <Crown className="w-8 h-8" /> : actualRank}
                    </motion.div>
                    <div className="flex-1">
                      <p className={`text-3xl font-bold ${isWinner ? 'text-[#FFD700]' : 'text-white'}`}>
                        {getTeamName(team)}
                      </p>
                      {actualRank <= 3 && (
                        <p className="text-sm text-[#E2D9F3] mt-1">
                          {actualRank === 1 ? '🥇 1st Place' : actualRank === 2 ? '🥈 2nd Place' : '🥉 3rd Place'}
                        </p>
                      )}
                    </div>
                    <div className="text-4xl font-bold text-white">
                      {team.score}
                      <span className="text-xl text-[#E2D9F3] ml-2">pts</span>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
          
          {/* Winner celebration */}
          <AnimatePresence>
            {showWinner && winner && (
              <motion.div
                initial={{ scale: 0, opacity: 0, y: 50 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 150, damping: 12 }}
                className="mt-12 text-center"
              >
                <motion.div 
                  animate={{ 
                    boxShadow: [
                      "0 0 20px rgba(255, 215, 0, 0.3)",
                      "0 0 60px rgba(255, 215, 0, 0.6)",
                      "0 0 20px rgba(255, 215, 0, 0.3)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="inline-block p-8 rounded-3xl bg-gradient-to-br from-[#FFD700]/40 to-[#FFA500]/20 border-4 border-[#FFD700]"
                >
                  <motion.div
                    animate={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                  >
                    <Trophy className="w-24 h-24 text-[#FFD700] mx-auto mb-4" />
                  </motion.div>
                  <h2 className="text-4xl font-bold text-[#FFD700] mb-2">🎉 WINNER 🎉</h2>
                  <motion.p 
                    className="text-5xl font-bold text-white"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    {getTeamName(winner)}
                  </motion.p>
                  <p className="text-3xl text-[#E2D9F3] mt-3">{winner.score} points</p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default PresentationView;
